// client/src/user/pages/ForgotPassword.js  (used as a standalone page)
import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  InputAdornment,
  alpha,
  useTheme,
  Alert,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from "@mui/material";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import KeyIcon from "@mui/icons-material/Key";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { GlobalContext } from "../../context/GlobalContext";
import { useHistory } from "react-router-dom";

const STEPS = ["Enter Email", "Verify OTP", "New Password"];

const ForgotPassword = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const history = useHistory();

  const [step, setStep] = useState(0);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSendOTP = async () => {
    if (!email) return setError("Please enter your email");
    setError("");
    setLoading(true);
    const res = await hitAxios({
      path: "/api/user/forgot-password",
      post: true,
      obj: { email },
      showSnackbar: false,
    });
    setLoading(false);
    if (res?.data?.success) {
      setStep(1);
      setSuccess("OTP sent to your email if it exists in our system.");
    } else {
      setError(res?.data?.msg || "Something went wrong");
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp) return setError("Please enter the OTP");
    setError("");
    setLoading(true);
    const res = await hitAxios({
      path: "/api/user/verify-reset-otp",
      post: true,
      obj: { email, otp },
      showSnackbar: false,
    });
    setLoading(false);
    if (res?.data?.success) {
      setStep(2);
      setSuccess("");
    } else {
      setError(res?.data?.msg || "Invalid OTP");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) return setError("Please enter a new password");
    if (newPassword !== confirmPassword)
      return setError("Passwords do not match");
    setError("");
    setLoading(true);
    const res = await hitAxios({
      path: "/api/user/reset-password",
      post: true,
      obj: { email, otp, new_password: newPassword },
      showSnackbar: false,
    });
    setLoading(false);
    if (res?.data?.success) {
      setSuccess("Password reset successfully! Redirecting to login...");
      setTimeout(() => history.push("/user/login"), 2000);
    } else {
      setError(res?.data?.msg || "Something went wrong");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background: isDark
          ? "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #0F172A 100%)"
          : "linear-gradient(135deg, #EEF2FF 0%, #F0F9FF 50%, #F5F3FF 100%)",
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            bgcolor: isDark
              ? alpha(theme.palette.background.paper, 0.8)
              : "background.paper",
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(isDark ? "#ffffff" : "#000000", 0.08)}`,
            boxShadow: isDark
              ? `0 24px 80px ${alpha("#000", 0.5)}`
              : `0 24px 60px ${alpha("#000", 0.1)}`,
          }}
        >
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                margin: "0 auto 16px",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              <KeyIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ letterSpacing: "-0.02em" }}
            >
              Forgot Password
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {step === 0 && "Enter your email to receive a reset code"}
              {step === 1 && "Enter the 6-digit code sent to your email"}
              {step === 2 && "Create your new password"}
            </Typography>
          </Box>

          <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{ "& .MuiStepLabel-label": { fontSize: "0.7rem" } }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          <Stack spacing={2}>
            {step === 0 && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSendOTP}
                  disabled={loading}
                  sx={{
                    py: 1.25,
                    fontWeight: 600,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  {loading ? "Sending..." : "Send Reset Code"}
                </Button>
              </>
            )}

            {step === 1 && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="6-digit OTP"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleVerifyOTP()}
                  inputProps={{
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      letterSpacing: "0.5rem",
                    },
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleVerifyOTP}
                  disabled={loading || otp.length !== 6}
                  sx={{ py: 1.25, fontWeight: 600, borderRadius: 2 }}
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  size="small"
                  onClick={() => {
                    setStep(0);
                    setOtp("");
                    setError("");
                    setSuccess("");
                  }}
                  sx={{ borderRadius: 2, color: "text.secondary" }}
                >
                  ← Back to email
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="New Password"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ fontSize: 18 }} />
                          ) : (
                            <Visibility sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Confirm New Password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                  error={!!confirmPassword && newPassword !== confirmPassword}
                  helperText={
                    !!confirmPassword && newPassword !== confirmPassword
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleResetPassword}
                  disabled={
                    loading || !newPassword || newPassword !== confirmPassword
                  }
                  sx={{ py: 1.25, fontWeight: 600, borderRadius: 2 }}
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </>
            )}

            <Button
              fullWidth
              variant="text"
              size="small"
              onClick={() => history.push("/user/login")}
              sx={{ borderRadius: 2, color: "text.secondary" }}
            >
              Back to Sign In
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
